import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Stack,
  TextField,
  IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import TimelineIcon from "@mui/icons-material/Timeline";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { getLoyaltyProfilesApi, getLoyaltyProfileHistoryApi } from "../../services/loyalty.service";
import { startLoader, stopLoader, showToast } from "../../lib/global";
import { breadcrumbAction } from "../../redux/actions/config.action";

const LoyaltyProfiles = () => {
  const dispatch = useDispatch();
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState({ profile: {}, transactions: [] });
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const breadcrumb = [{ label: "Loyalty Profiles", url: null }];
    dispatch(breadcrumbAction(breadcrumb));
  }, [dispatch]);

  const fetchProfiles = async () => {
    startLoader();
    try {
      const response = await getLoyaltyProfilesApi({
        page,
        limit,
        search: searchTerm,
      });
      if (response?.data) {
        setProfiles(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (err) {
      console.error(err);
      showToast("Error loading loyalty profiles", "error");
    } finally {
      stopLoader();
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [page, searchTerm]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(search);
  };

  const handleResetSearch = () => {
    setSearch("");
    setSearchTerm("");
    setPage(1);
  };

  const handleViewHistory = async (consumerId) => {
    setHistoryLoading(true);
    setHistoryOpen(true);
    try {
      const response = await getLoyaltyProfileHistoryApi(consumerId);
      if (response?.data) {
        setHistoryData(response.data);
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to fetch transaction history", "error");
      setHistoryOpen(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <div className="loyalty-profiles-admin mx-3">
      {/* Search Header */}
      <div className="card p-4 mb-4 bg-white shadow-sm rounded border-0">
        <h5 className="mb-3 font-weight-bold text-dark d-flex align-items-center">
          <LocalActivityIcon className="mr-2 text-primary" /> Loyalty Member Profiles
        </h5>
        <form onSubmit={handleSearchSubmit} className="d-flex align-items-center flex-wrap gap-2">
          <div className="position-relative flex-grow-1 min-width-300" style={{ maxWidth: "500px" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search by student ID, parent WA, name, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon className="text-muted mr-1" size="small" />
              }}
            />
          </div>
          <button type="submit" className="btn btn-secondary px-4 ml-2">
            Search
          </button>
          <button type="button" className="btn btn-outline-secondary px-4 ml-2" onClick={handleResetSearch}>
            Reset
          </button>
        </form>
      </div>

      {/* Main List Table */}
      <div className="card p-3 bg-white shadow-sm rounded border-0">
        <TableContainer component={Paper} elevation={0} className="border">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="font-weight-bold bg-light">No.</TableCell>
                <TableCell className="font-weight-bold bg-light">Customer Name</TableCell>
                <TableCell className="font-weight-bold bg-light">Email / Phone</TableCell>
                <TableCell className="font-weight-bold bg-light">Student ID</TableCell>
                <TableCell className="font-weight-bold bg-light">Parent WhatsApp</TableCell>
                <TableCell className="font-weight-bold bg-light" align="center">Status</TableCell>
                <TableCell className="font-weight-bold bg-light" align="right">Points Balance</TableCell>
                <TableCell className="font-weight-bold bg-light" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles.length > 0 ? (
                profiles.map((row, index) => (
                  <TableRow key={row.profile_id} hover>
                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                    <TableCell className="font-weight-bold">{row.name}</TableCell>
                    <TableCell>
                      <div>{row.email || <span className="text-muted italic">No Email (Temp)</span>}</div>
                      <div className="small text-muted">{row.phone}</div>
                    </TableCell>
                    <TableCell>{row.student_id || "-"}</TableCell>
                    <TableCell>{row.parent_whatsapp || "-"}</TableCell>
                    <TableCell align="center">
                      {row.verification_status === "VERIFIED" ? (
                        <span className="badge badge-success px-2 py-1 align-items-center d-inline-flex gap-1" style={{ fontSize: "0.8rem", color: "#fff" }}>
                          <CheckCircleIcon style={{ fontSize: "0.9rem" }} /> VERIFIED
                        </span>
                      ) : (
                        <span className="badge badge-warning px-2 py-1 align-items-center d-inline-flex gap-1 text-dark" style={{ fontSize: "0.8rem", backgroundColor: "#ffeeba" }}>
                          <HourglassEmptyIcon style={{ fontSize: "0.9rem" }} /> PENDING
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="right" className="font-weight-bold text-primary" style={{ fontSize: "1rem" }}>
                      {row.points_balance} pts
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewHistory(row.consumer_id)}
                      >
                        History
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" className="text-muted py-5">
                    No loyalty profiles found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {total > 0 && (
          <div className="d-flex justify-content-end mt-4">
            <Stack spacing={2}>
              <Pagination
                count={Math.ceil(total / limit)}
                page={page}
                shape="rounded"
                color="primary"
                onChange={handlePageChange}
              />
            </Stack>
          </div>
        )}
      </div>

      {/* Transaction History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          style: { borderRadius: "12px" }
        }}
      >
        <DialogTitle className="d-flex justify-content-between align-items-center border-bottom bg-light">
          <div className="d-flex align-items-center">
            <TimelineIcon className="mr-2 text-primary" style={{ marginRight: "8px" }} />
            <span>Transaction History: <strong>{historyData.profile?.name || "Loading..."}</strong></span>
          </div>
          <IconButton onClick={() => setHistoryOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="py-3">
          {historyLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading transactions...</p>
            </div>
          ) : (
            <div>
              {/* Member Metadata Overview */}
              <div className="row mb-4 p-3 bg-light rounded mx-0 border">
                <div className="col-md-3 mb-2 mb-md-0">
                  <span className="small text-muted d-block">Points Balance</span>
                  <strong className="text-primary" style={{ fontSize: "1.25rem" }}>
                    {historyData.profile?.points_balance || 0} pts
                  </strong>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <span className="small text-muted d-block">Verification Status</span>
                  {historyData.profile?.verification_status === "VERIFIED" ? (
                    <span className="badge badge-success px-2 py-1 mt-1 align-items-center d-inline-flex gap-1" style={{ fontSize: "0.75rem", color: "#fff" }}>
                      VERIFIED
                    </span>
                  ) : (
                    <span className="badge badge-warning px-2 py-1 mt-1 align-items-center d-inline-flex gap-1 text-dark" style={{ fontSize: "0.75rem", backgroundColor: "#ffeeba" }}>
                      PENDING
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <span className="small text-muted d-block">WhatsApp Number</span>
                  <span>{historyData.profile?.parent_whatsapp || "-"}</span>
                </div>
                <div className="col-md-3">
                  <span className="small text-muted d-block">Registered At</span>
                  <span>{historyData.profile?.created_at ? new Date(historyData.profile?.created_at).toLocaleDateString() : "-"}</span>
                </div>
              </div>

              {/* Transactions List */}
              <h6 className="mb-3 font-weight-bold">Transaction History Log</h6>
              <TableContainer component={Paper} elevation={0} className="border">
                <Table size="small">
                  <TableHead className="bg-light">
                    <TableRow>
                      <TableCell className="font-weight-bold">No.</TableCell>
                      <TableCell className="font-weight-bold">Type</TableCell>
                      <TableCell className="font-weight-bold" align="right">Purchase Amount</TableCell>
                      <TableCell className="font-weight-bold" align="right">Points Effect</TableCell>
                      <TableCell className="font-weight-bold">POS Reference</TableCell>
                      <TableCell className="font-weight-bold">Sync Status</TableCell>
                      <TableCell className="font-weight-bold">Status</TableCell>
                      <TableCell className="font-weight-bold">Date & Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.transactions?.length > 0 ? (
                      historyData.transactions.map((tx, idx) => (
                        <TableRow key={tx.transaction_id} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            {tx.type === "EARN" ? (
                              <span className="text-success font-weight-bold">EARN</span>
                            ) : (
                              <span className="text-danger font-weight-bold">REDEEM</span>
                            )}
                          </TableCell>
                          <TableCell align="right">RM {parseFloat(tx.amount || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" className={tx.type === "EARN" ? "text-success font-weight-bold" : "text-danger font-weight-bold"}>
                            {tx.type === "EARN" ? `+${tx.points}` : `-${tx.points}`} pts
                          </TableCell>
                          <TableCell><code>{tx.pos_ref || "-"}</code></TableCell>
                          <TableCell>
                            {tx.sync_status === "CREDITED" ? (
                              <span className="badge badge-pill badge-light text-success border border-success px-2 py-1">Credited</span>
                            ) : tx.sync_status === "PENDING_SYNC" ? (
                              <span className="badge badge-pill badge-light text-warning border border-warning px-2 py-1">Pending Sync</span>
                            ) : (
                              <span className="badge badge-pill badge-light text-danger border border-danger px-2 py-1">{tx.sync_status}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`small font-weight-bold ${tx.status === "SUCCESS" ? "text-success" : "text-danger"}`}>
                              {tx.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted small">
                            {tx.created_at ? new Date(tx.created_at).toLocaleString() : "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" className="text-muted py-4">
                          No transactions recorded for this member.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </DialogContent>
        <DialogActions className="border-top bg-light">
          <Button onClick={() => setHistoryOpen(false)} color="secondary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LoyaltyProfiles;
